import type { BillingCategory, BillingLine, BillingMeter, BillingSettlement } from './api';

// The Billing page's "Year to date": the year's settled months added up into
// one settlement, so the page shows it like a month. Web-only (the mobile tab
// has no such period). Only type imports, so `node --test` runs it
// (tests/billingYear.test.ts).

const sum = (values: (number | undefined)[]): number | undefined =>
  values.some((v) => v != null) ? values.reduce<number>((a, v) => a + (v ?? 0), 0) : undefined;

const same = <T>(values: T[]): T | undefined => (values.every((v) => v === values[0]) ? values[0] : undefined);

// The year a list of settled months reaches: its newest month's, when that is
// a month (not a pre-settlement or another cycle).
export function latestYear(list: BillingSettlement[]): number | null {
  const m = /^(\d{4})-\d{2}$/.exec(list[0]?.label ?? '');
  return m ? Number(m[1]) : null;
}

// The settled months of `year` (labels "YYYY-MM", never a pre-settlement) as one
// settlement labelled "ytd-YYYY", a label no Settlement period has: each bill's
// amount and its lines' quantities and amounts summed, a line's price kept only
// while it never changed, corrections kept one by one, and each meter read
// from its first month's opening to its last month's closing. The association's averages are per month, so the year
// has none. Null when no month of the year is settled.
export function yearToDate(list: BillingSettlement[], year: number): BillingSettlement | null {
  const months = list
    .filter((s) => !s.pre_settlement && s.label.startsWith(`${year}-`) && /^\d{4}-\d{2}$/.test(s.label))
    .sort((a, b) => a.label.localeCompare(b.label));
  if (months.length === 0) return null;
  const first = months[0];
  const last = months[months.length - 1];

  const categories = new Map<string, BillingCategory[]>();
  for (const s of months) {
    for (const c of s.categories) categories.set(c.code, [...(categories.get(c.code) ?? []), c]);
  }

  const meters = new Map<string, BillingMeter[]>();
  for (const s of months) {
    for (const m of s.meters) {
      const key = m.serial || m.name;
      meters.set(key, [...(meters.get(key) ?? []), m]);
    }
  }

  return {
    label: `ytd-${year}`,
    from: first.from,
    to: last.to,
    period_from: first.period_from,
    period_to: last.period_to,
    settled_on: last.settled_on,
    pre_settlement: false,
    money: months.every((s) => s.money),
    currency: first.currency,
    total: sum(months.map((s) => s.total)),
    vat: sum(months.map((s) => s.vat)),
    categories: [...categories.values()].map((cs) => {
      const latest = cs[cs.length - 1];
      return {
        code: latest.code,
        name: latest.name,
        kind: latest.kind,
        amount: sum(cs.map((c) => c.amount)),
        lines: mergeLines(cs.flatMap((c) => c.lines)),
      };
    }),
    meters: [...meters.values()].map((ms) => {
      const a = ms[0];
      const z = ms[ms.length - 1];
      return {
        ...z,
        opening: a.opening,
        opening_date: a.opening_date,
        usage: sum(ms.map((m) => m.usage)),
      };
    }),
    averages: [],
  };
}

function mergeLines(lines: BillingLine[]): BillingLine[] {
  const out: BillingLine[] = [];
  const groups = new Map<string, BillingLine[]>();
  for (const l of lines) {
    if (l.kind === 'correction') {
      out.push(l);
      continue;
    }
    const key = `${l.kind}|${l.fee}|${l.name}|${l.unit ?? ''}`;
    const group = groups.get(key);
    if (group) group.push(l);
    else {
      const fresh = [l];
      groups.set(key, fresh);
      out.push(l);
    }
  }
  return out.map((l) => {
    if (l.kind === 'correction') return l;
    const g = groups.get(`${l.kind}|${l.fee}|${l.name}|${l.unit ?? ''}`) as BillingLine[];
    return {
      ...l,
      quantity: sum(g.map((x) => x.quantity)),
      amount: sum(g.map((x) => x.amount)),
      rate: same(g.map((x) => x.rate)),
      vat_rate: same(g.map((x) => x.vat_rate)),
      vat_shown: g.some((x) => x.vat_shown),
      estimated: g.some((x) => x.estimated),
    };
  });
}
